import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'

export default function OmOs() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/pages')
      .then(res => {
        if (!res.ok) throw new Error('Kunne ikke hente data')
        return res.json()
      })
      .then(data => {
        const omOs = data.find(p => p.page_slug === 'om-os')
        setPageData(omOs)
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-brand-orange"></i>
          <p className="text-slate-500 font-medium">Indlæser indhold...</p>
        </div>
      </div>
    )
  }

  // Fallback data matching the scrape exactly if CMS fetch fails
  const fallbackData = {
    title: "Om os",
    meta_title: "Om os - Trapezdk",
    meta_description: "Hos Trapezdk beskæftiger vi os med salg og montage af selvbærende højprofiler også kaldet trapezplader.",
    content: `<h1>Trapezdk</h1><p>Hos Trapezdk beskæftiger vi os med salg og montage af selvbærende højprofiler også kaldet trapezplader.</p><p>Vi har i flere år kun monteret højprofiler og igennem løbende erfaring udviklet os til en stærk partner i levering og montering af disse.</p><p>Vi sørger altid for at vores medarbejdere løbende er klædt på til opgaverne både ift. sikkerhed når man laver farligt arbejde, statiske godkendelser, kvalitetssikring løbende og bygningsreglementet.</p><p>Vi lægger høj vægt på kvalitet i udført arbejde og kunne efterlevere en ks rapport som underbygger dette.</p><p>Vi ser frem til at være din sparingspartner på dit næste projekt</p>`,
    images: ["https://assets.auxodev.dk/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6783c0ccbd2c1504a5d95ba6-ny-20brandstation-20gr-c3-b8nland.jpeg"]
  }

  const data = pageData || fallbackData

  return (
    <div className="w-full">
      <Helmet>
        <title>{data.meta_title || "Om os - Trapezdk"}</title>
        <meta name="description" content={data.meta_description || "Hos Trapezdk beskæftiger vi os med salg og montage af selvbærende højprofiler også kaldet trapezplader."} />
        <meta property="og:title" content={data.meta_title || "Om os - Trapezdk"} />
        <meta property="og:description" content={data.meta_description || "Hos Trapezdk beskæftiger vi os med salg og montage af selvbærende højprofiler også kaldet trapezplader."} />
        <meta property="og:image" content={data.images?.[0] || ""} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Page Header */}
      <section className="relative text-white py-24 lg:py-32 overflow-hidden bg-slate-950">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6784d4ee8815f9c670344925-tts-20sag.jpg" 
            alt="Trapezplader baggrund" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/85 to-slate-950/80"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-left">
          <span className="text-brand-orange font-bold text-sm tracking-wider uppercase">Hvem er vi</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-2">Om os</h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Left Column: Image */}
            <div className="lg:col-span-5 relative flex flex-col">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-brand-orange to-amber-500 rounded-none blur opacity-25"></div>
              <div className="relative bg-slate-800 rounded-none overflow-hidden shadow-2xl border border-slate-200 flex-1 flex flex-col">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/6783c0ccbd2c1504a5d95ba6-ny-20brandstation-20gr-c3-b8nland.jpeg" 
                  alt="Ny brandstation Grønland" 
                  className="w-full h-full object-cover min-h-[300px] lg:min-h-0 flex-1 rounded-none"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-none text-white text-xs font-medium">
                  Reference: Ny brandstation Grønland
                </div>
              </div>
            </div>

            {/* Right Column: Text Content (Verbatim from Scrape) */}
            <div className="lg:col-span-7 text-left space-y-6">
              <h2 className="text-3xl font-extrabold text-brand-blue">Trapezdk</h2>
              <div className="h-1 w-16 bg-brand-orange rounded-full mb-6"></div>
              
              <div className="space-y-6 text-slate-600 leading-relaxed text-base">
                <p className="font-semibold text-slate-900 text-lg">
                  Hos Trapezdk beskæftiger vi os med salg og montage af selvbærende højprofiler også kaldet trapezplader.
                </p>
                <p>
                  Vi har i flere år kun monteret højprofiler og igennem løbende erfaring udviklet os til en stærk partner i levering og montering af disse.
                </p>
                <p>
                  Vi sørger altid for at vores medarbejdere løbende er klædt på til opgaverne både ift. sikkerhed når man laver farligt arbejde, statiske godkendelser, kvalitetssikring løbende og bygningsreglementet.
                </p>
                <p>
                  Vi lægger høj vægt på kvalitet i udført arbejde og kunne efterlevere en ks rapport som underbygger dette.
                </p>
                <p className="font-medium text-brand-orange text-lg">
                  Vi ser frem til at være din sparingspartner på dit næste projekt
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 bg-slate-50 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 text-left space-y-4">
              <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center text-brand-orange">
                <i className="fa-solid fa-helmet-safety text-xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 text-lg">Sikkerhed i fokus</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Vores medarbejdere er løbende klædt på til opgaverne ift. sikkerhed, især når der udføres farligt arbejde i højden.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 text-left space-y-4">
              <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center text-brand-orange">
                <i className="fa-solid fa-file-signature text-xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 text-lg">Kvalitetssikring (KS)</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Vi lægger høj vægt på kvalitet i udført arbejde og efterleverer altid en KS-rapport, som underbygger og dokumenterer dette.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 text-left space-y-4">
              <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center text-brand-orange">
                <i className="fa-solid fa-gavel text-xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 text-lg">Bygningsreglementet</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Vi sikrer fuld overholdelse af bygningsreglementet samt statiske godkendelser på alle vores monteringsprojekter.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
